import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useChat } from '@devdept/samolet-ui/lib/context/chat';
import AndroidOutlined from '@ant-design/icons/lib/icons/AndroidOutlined';
import { useSelector } from 'react-redux';
import { CHAT_KINDS_PROJECT } from 'constants/chat';
import { LOGIN } from 'constants/routes';
import { LayoutProps } from 'components/Layout';
import {
  AsideMenuItemsKeys,
  asideMenuItems,
  asideMenuItemsRoutes,
} from 'features/AsideMenu';
import { asideMenuItemsValues } from 'features/AsideMenu/types';
import { isEmpty } from 'helpers/object';
import { useProfile, useProfileStorage } from 'hooks/profile';
import { useAuth } from 'context/Auth';
import useAnalyticsLogger, {
  EAnalyticsLoggerEventNames,
  caseFactory,
} from 'controllers/loggers/AnalitycsLoggerController/hooks';
import { useCheckVersion } from 'features/VersionChecker/useCheckVersion';
import { selectFeature } from 'store/common/toggle';

const content: LayoutProps['content'] = {
  withScroller: true,
};

export const useLayout = (): LayoutProps => {
  const { profile, isAnonymous } = useProfileStorage();
  const { permissions } = useProfile();
  const { token, provider } = useAuth();
  const { appVersion } = useCheckVersion();
  const logger = useAnalyticsLogger();
  const { isEnabled: isChatHidden } = useSelector(selectFeature('hideChat'));
  const { isEnabled: isRegistrationButtonHidden } = useSelector(
    selectFeature('hideRegistrationButton'),
  );

  const chatBase = useChat();
  const chat = useMemo(
    () => ({ ...chatBase, kindsProject: CHAT_KINDS_PROJECT }),
    [chatBase],
  );
  const { pathname } = location;

  const [selectedAsideItem, setSelectedAsideItem] = useState<AsideMenuItemsKeys>(
    AsideMenuItemsKeys.COMPANY,
  );

  useEffect(() => {
    const routes = Object.values(asideMenuItemsRoutes);
    const key = routes.findIndex(route => pathname.startsWith(route));
    setSelectedAsideItem(Object.keys(asideMenuItemsRoutes)[key] as AsideMenuItemsKeys);
  }, [pathname]);

  const headerProps: LayoutProps['header'] = useMemo(
    () => ({
      ...appVersion,
      jwt: token,
      navigationRequestOptions: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      chat: chat,
      hideChat: isChatHidden,
      hideRegistrationButton: isRegistrationButtonHidden,
      links: profile?.isSuperuser
        ? [{ link: '/admin/', text: 'Админка', icon: <AndroidOutlined /> }]
        : [],
      profile,
      loginUrl: LOGIN,
      withoutLoginRedirect: true,
      showError: false,
      ...provider.config,
    }),
    [
      appVersion,
      token,
      chat,
      isChatHidden,
      isRegistrationButtonHidden,
      profile,
      provider.config,
    ],
  );

  const menuItems = useMemo(
    () =>
      asideMenuItems({
        employee: Boolean(profile?.isEmployee),
        notEmployee: Boolean(!isEmpty(profile) && !profile.isEmployee),
        permissions,
        isAnonymous,
        onClick: ({ key }) => {
          logger.log(
            caseFactory({ name: EAnalyticsLoggerEventNames.LeftPanelClick }).context(
              asideMenuItemsValues[key as keyof typeof AsideMenuItemsKeys],
            ),
          );
        },
      }),
    [profile, permissions, isAnonymous, logger],
  );

  const onSelectAsideItem = useCallback(e => {
    setSelectedAsideItem(e.key);
  }, []);

  const sider: LayoutProps['sider'] = useMemo(
    () => ({
      defaultCollapsed: true,
      menu: menuItems,
      projectTitle: 'S.Tender',
      menuProps: {
        onSelect: onSelectAsideItem,
        selectedKeys: [selectedAsideItem],
      },
    }),
    [menuItems, selectedAsideItem, onSelectAsideItem],
  );

  return useMemo(
    () => ({
      content,
      header: headerProps,
      sider,
      chat,
    }),
    [headerProps, chat, sider],
  );
};
